'use client';

import * as React from 'react';
import { ChevronDown, CircleUser } from 'lucide-react';

import { cn } from '../../lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { useOptionalGLPSidebar } from './glp-sidebar';

export interface GLPUserProfileUser {
  /** Display name. */
  name?: string;
  /** Email — falls back into the trigger when name is missing. */
  email?: string;
  /** Sub-line displayed on the trigger and dropdown header. Often a company name. */
  companyName?: string;
  /** Optional tertiary info (e.g. mirrored company) shown only in the dropdown header. */
  description?: React.ReactNode;
  /** Avatar image URL. */
  avatarSrc?: string;
  /** Initials shown when no avatar image is supplied. Auto-derived from `name` if absent. */
  initials?: string;
  /** Optional role string the consumer can use to gate actions outside this component. */
  role?: string;
}

export type GLPUserProfileMenuItem =
  | {
      type?: 'item';
      key?: string;
      label: React.ReactNode;
      icon?: React.ComponentType<{ className?: string }>;
      onSelect?: () => void;
      disabled?: boolean;
      /** When true the item renders in destructive (red) styling. */
      danger?: boolean;
      /**
       * If true, calling onSelect will NOT close the dropdown automatically. Useful for
       * toggle items (e.g. Lock Sidebar) that should stay open.
       */
      keepOpen?: boolean;
    }
  | { type: 'separator'; key?: string }
  | { type: 'custom'; key?: string; render: () => React.ReactNode };

export type GLPUserProfileMenuSection = GLPUserProfileMenuItem[];

export interface GLPUserProfileProps {
  user: GLPUserProfileUser;
  /** Sections of menu items. Each section is rendered with a separator between sections. */
  menuSections?: GLPUserProfileMenuSection[];
  /** Override the auto-detected sidebar collapsed state. */
  collapsed?: boolean;
  /** Optional content rendered at the very top of the dropdown (replaces the default user header). */
  dropdownHeader?: React.ReactNode;
  /** Optional content rendered at the very bottom of the dropdown. */
  dropdownFooter?: React.ReactNode;
  /** Custom error/no-data state (e.g. when the user fetch failed). */
  errorState?: React.ReactNode;
  /** Forwarded to the outer container. */
  className?: string;
  /** Tone for the trigger label. Defaults to 'inverse' (white-on-primary, like the GLP main sidebar). */
  tone?: 'inherit' | 'inverse';
  /** Side the menu opens on. Defaults to 'right' when collapsed, else 'top' (sidebar footer). */
  menuSide?: 'top' | 'right' | 'bottom' | 'left';
  /** Menu alignment against the trigger. Defaults to 'start'. */
  menuAlign?: 'start' | 'center' | 'end';
}

/**
 * The user's photo when `src` is given and loads; otherwise the person icon.
 * A broken URL falls back to the icon instead of a broken-image glyph.
 */
function ProfileAvatar({
  src,
  className,
  iconClassName,
}: {
  src?: string;
  className?: string;
  iconClassName?: string;
}) {
  const [failedSrc, setFailedSrc] = React.useState<string | undefined>();
  if (src && failedSrc !== src) {
    return (
      <img
        src={src}
        alt=''
        className={cn('rounded-full object-cover', className)}
        onError={() => setFailedSrc(src)}
      />
    );
  }
  return <CircleUser className={iconClassName} />;
}

/**
 * GLPUserProfile — sidebar bottom user widget.
 *
 * Renders a person icon + chevron trigger that opens a dropdown menu with
 * the consumer's items. The chevron rotates to indicate open/closed state.
 * The dropdown header shows person icon + name + email.
 */
export function GLPUserProfile({
  user,
  menuSections = [],
  collapsed: collapsedProp,
  dropdownHeader,
  dropdownFooter,
  errorState,
  className,
  tone = 'inverse',
  menuSide,
  menuAlign = 'start',
}: GLPUserProfileProps) {
  // Auto-detect collapsed state from the GLP sidebar context when available.
  // `useOptionalGLPSidebar` returns null when the widget is mounted outside a
  // <GLPSidebarProvider>, which is supported for testing/preview use cases.
  const sidebarCtx = useOptionalGLPSidebar();
  const collapsed = collapsedProp ?? sidebarCtx?.collapsed ?? false;

  if (errorState) {
    return <div className={cn('px-3 py-2', className)}>{errorState}</div>;
  }

  const triggerName = user.name || user.email || '';
  const triggerInverse = tone === 'inverse';

  const renderMenuItem = (item: GLPUserProfileMenuItem, idx: number, isLastInSection: boolean) => {
    if (item.type === 'separator') {
      return <DropdownMenuSeparator key={item.key ?? `sep-${idx}`} />;
    }
    if (item.type === 'custom') {
      return (
        <React.Fragment key={item.key ?? `custom-${idx}`}>
          {item.render()}
          {!isLastInSection && <DropdownMenuSeparator />}
        </React.Fragment>
      );
    }
    const Icon = item.icon;
    return (
      <React.Fragment key={item.key ?? `item-${idx}`}>
        <DropdownMenuItem
          disabled={item.disabled}
          onSelect={(e) => {
            if (item.keepOpen) e.preventDefault();
            item.onSelect?.();
          }}
          className={cn(
            'cursor-pointer flex items-center gap-2',
            item.danger && 'text-red-600 focus:text-red-700 focus:bg-red-50',
          )}
        >
          {Icon && <Icon className='h-4 w-4 flex-shrink-0' />}
          <span>{item.label}</span>
        </DropdownMenuItem>
        {!isLastInSection && <DropdownMenuSeparator />}
      </React.Fragment>
    );
  };

  return (
    <div className={cn('w-full', className)}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type='button'
            aria-label='Open profile menu'
            className={cn(
              'group flex items-center gap-2.5 w-full px-2 py-2 rounded-lg transition-colors',
              triggerInverse
                ? 'text-accessible-text-color hover:bg-white/10'
                : 'text-foreground hover:bg-muted',
            )}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {/* Avatar */}
            <span
              className={cn(
                'inline-flex items-center justify-center rounded-full h-9 w-9 flex-shrink-0',
                triggerInverse ? 'bg-white/15' : 'bg-muted',
              )}
            >
              <ProfileAvatar
                src={user.avatarSrc}
                className='h-9 w-9'
                iconClassName='h-6 w-6'
              />
            </span>

            {/* User Info - only show when not collapsed */}
            {!collapsed && (
              <div className='flex-1 min-w-0 text-left'>
                <p className='text-sm font-semibold truncate'>{user.name || user.email}</p>
                {user.companyName && (
                  <p className='text-xs opacity-70 truncate'>{user.companyName}</p>
                )}
              </div>
            )}

            {/* Chevron */}
            <ChevronDown
              className={cn(
                'h-4 w-4 flex-shrink-0 opacity-70 transition-transform duration-200',
                'group-data-[state=open]:rotate-180',
              )}
            />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          className='w-64 max-w-[90vw]'
          side={menuSide ?? (collapsed ? 'right' : 'top')}
          align={menuAlign}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {dropdownHeader ?? (
            <div className='px-3 py-3 flex items-center gap-3'>
              <span className='inline-flex items-center justify-center rounded-full  bg-[hsl(var(--color-primary)/0.1)] text-[hsl(var(--color-primary))] flex-shrink-0'>
                <ProfileAvatar
                  src={user.avatarSrc}
                  className='h-8 w-8'
                  iconClassName='text-global-green text-[32px]'
                />
              </span>
              <div className='min-w-0 leading-tight flex-1'>
                {triggerName && (
                  <p className='text-sm font-medium text-foreground truncate'>{triggerName}</p>
                )}
                {user.email && (
                  <p className='text-xs text-muted-foreground truncate'>{user.email}</p>
                )}
                {user.description && (
                  <div className='text-xs mt-0.5 text-muted-foreground'>{user.description}</div>
                )}
              </div>
            </div>
          )}

          {menuSections.length > 0 && <DropdownMenuSeparator />}

          {menuSections.map((section, sectionIdx) => {
            const isLastSection = sectionIdx === menuSections.length - 1;
            return (
              <React.Fragment key={`section-${sectionIdx}`}>
                {section.map((item, itemIdx) => {
                  const isLastInSection = itemIdx === section.length - 1;
                  // Suppress the trailing separator for the very last item in the
                  // very last section — the section-level separator below handles it.
                  const dropTrailing = isLastInSection;
                  return renderMenuItem(item, itemIdx, dropTrailing);
                })}
                {!isLastSection && <DropdownMenuSeparator />}
              </React.Fragment>
            );
          })}

          {dropdownFooter && (
            <>
              <DropdownMenuSeparator />
              {dropdownFooter}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
